import {
  ColumnDefinition,
  MIXED_COLUMNS,
  SOURCES_COLUMNS,
  StaticListDataSource,
  StaticListItem,
  StaticListItemType,
  WORKSPACE_DOCUMENT_COLUMNS,
} from "@/models/dashboard/WidgetConfig";

/** Separator between the two halves of a legislation item's originalId. */
const LEGISLATION_ID_SEPARATOR = "|";

/**
 * Legislation items store their identity as "externalId|datasource" because a
 * document id is only unique within its datasource. Workspace documents store
 * a bare document id, which is what tells the two apart in a mixed list.
 */
export const buildLegislationOriginalId = (
  externalId: string,
  datasource: string
): string => `${externalId}${LEGISLATION_ID_SEPARATOR}${datasource}`;

export const parseLegislationOriginalId = (
  originalId: string
): { externalId: string; datasource: string } | null => {
  const parts = originalId.split(LEGISLATION_ID_SEPARATOR);
  if (parts.length !== 2) return null;

  const [externalId, datasource] = parts;
  if (!externalId || !datasource) return null;

  return { externalId, datasource };
};

/**
 * Which kind of document an item points at.
 *
 * Items added since mixed lists shipped carry an explicit `itemType`. Older
 * configs don't, so a single-kind widget lends its own dataSource to every
 * item, and a widget that was widened to "mixed" falls back to the shape of
 * the stored id.
 */
export const resolveStaticListItemType = (
  item: StaticListItem,
  dataSource: StaticListDataSource
): StaticListItemType => {
  if (item.itemType) return item.itemType;
  if (dataSource !== "mixed") {
    return dataSource === "sources" ? "source" : "workspace-document";
  }
  if (item.workspaceId) return "workspace-document";
  return parseLegislationOriginalId(item.originalId)
    ? "source"
    : "workspace-document";
};

export const getStaticListColumns = (
  dataSource: StaticListDataSource
): ColumnDefinition[] => {
  if (dataSource === "sources") return SOURCES_COLUMNS;
  if (dataSource === "workspace-documents") return WORKSPACE_DOCUMENT_COLUMNS;
  return MIXED_COLUMNS;
};

/** Whether a widget on `dataSource` is allowed to hold items of `itemType`. */
const acceptsItemType = (
  dataSource: StaticListDataSource,
  itemType: StaticListItemType
): boolean => {
  if (dataSource === "mixed") return true;
  return dataSource === "sources"
    ? itemType === "source"
    : itemType === "workspace-document";
};

export const sortStaticListItems = (
  items: StaticListItem[]
): StaticListItem[] => [...items].sort((a, b) => a.sortOrder - b.sortOrder);

/** Split a list into the two query paths that resolve item metadata. */
export const partitionStaticListItems = (
  items: StaticListItem[],
  dataSource: StaticListDataSource
): {
  sourceItems: StaticListItem[];
  workspaceDocumentItems: StaticListItem[];
} => {
  const sourceItems: StaticListItem[] = [];
  const workspaceDocumentItems: StaticListItem[] = [];

  items.forEach((item) => {
    if (resolveStaticListItemType(item, dataSource) === "source") {
      sourceItems.push(item);
    } else {
      workspaceDocumentItems.push(item);
    }
  });

  return { sourceItems, workspaceDocumentItems };
};

/**
 * Carry the items across a change of dataSource: keep the ones the new
 * dataSource accepts, stamp each with its now-explicit type, and renumber
 * sortOrder over the survivors. Widening to "mixed" therefore keeps
 * everything, and narrowing keeps the matching half — switching between the
 * two single-kind sources leaves nothing behind, as it always has.
 */
export const migrateStaticListItems = (
  items: StaticListItem[],
  from: StaticListDataSource,
  to: StaticListDataSource
): StaticListItem[] => {
  if (from === to) return items;

  return sortStaticListItems(items)
    .map((item) => ({
      ...item,
      itemType: resolveStaticListItemType(item, from),
    }))
    .filter((item) => acceptsItemType(to, item.itemType))
    .map((item, index) => ({ ...item, sortOrder: index }));
};

/**
 * The fields a cell can read. Structurally satisfied by the hook's
 * ResolvedItemInfo; kept local so this module stays free of data-fetching.
 */
export interface StaticListResolvedInfo {
  title?: string;
  type?: string;
  status?: string;
  workspaceName?: string;
  updatedAt?: string;
  createdAt?: string;
  datasource?: string;
  date?: string;
  internalId?: string;
}

/**
 * Text for one cell.
 *
 * The mixed column set has to read for both kinds of row, so `source` folds
 * together the legislation datasource and the workspace name, `date` folds
 * together the published date and the last-updated date, and `title` also
 * covers what the workspace-only set calls `name`.
 */
export const getStaticListCellValue = (
  item: StaticListItem,
  columnId: string,
  itemType: StaticListItemType,
  resolved: StaticListResolvedInfo
): string => {
  if (columnId === "origin") {
    return itemType === "source" ? "Kilder" : "Workspace";
  }
  if (columnId === "source") {
    return itemType === "source"
      ? resolved.datasource || "-"
      : resolved.workspaceName || item.workspaceName || "-";
  }

  if (itemType === "source") {
    switch (columnId) {
      case "title":
        return item.customName || resolved.title || "-";
      case "type":
        return resolved.type || "-";
      case "datasource":
        return resolved.datasource || "-";
      case "date":
        return resolved.date || "-";
      case "status":
        return resolved.status || "-";
      default:
        return "-";
    }
  }

  switch (columnId) {
    case "name":
    case "title":
      return item.customName || resolved.title || "-";
    case "workspace":
      return resolved.workspaceName || item.workspaceName || "-";
    case "type":
      return resolved.type || "-";
    case "updatedAt":
    case "date":
      return resolved.updatedAt || "-";
    case "createdAt":
      return resolved.createdAt || "-";
    default:
      return "-";
  }
};
